import React from "react";
import { v4 as uuidv4 } from "uuid";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import { isHTMLElement } from "../../utils/isHTMLElement";
import type { PopoverAriaRole } from "../Popover";
interface UseDropdownButtonOptions {
    trapFocus: boolean;
    dropdownAriaRole: PopoverAriaRole;
}
export function useDropdownButton(options: UseDropdownButtonOptions): DropdownButtonState {
    const [isOpen, setIsOpen] = React.useState(false);
    return useOpenControlledDropdownButton({ ...options, isOpen, setIsOpen });
}
interface UseOpenControlledDropdownButtonOptions extends UseDropdownButtonOptions {
    isOpen: boolean;
    setIsOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
}
export function useOpenControlledDropdownButton({ trapFocus, dropdownAriaRole, setIsOpen, isOpen }: UseOpenControlledDropdownButtonOptions): DropdownButtonState {
    const id = React.useRef(`dropdown-${uuidv4()}`);
    const [buttonElement, setButtonElement] = React.useState<HTMLButtonElement | null>(null);
    const [dropdownElement, setDropdownElement] = React.useState<HTMLDivElement | null>(null);
    const setFocusTrapElement = useFocusTrap();
    const onCloseSubscribers = React.useRef<CloseDropdownCallback[]>([]);
    const onOpenSubscribers = React.useRef<OpenDropdownCallback[]>([]);
    const subscribeClose = React.useCallback((subscriber: CloseDropdownCallback) => {
        onCloseSubscribers.current.push(subscriber);
        return () => (onCloseSubscribers.current = onCloseSubscribers.current.filter((prev) => prev !== subscriber));
    }, []);
    const subscribeOpen = React.useCallback((subscriber: OpenDropdownCallback) => {
        onOpenSubscribers.current.push(subscriber);
        return () => {
            onOpenSubscribers.current = onOpenSubscribers.current.filter((prev) => prev !== subscriber);
        };
    }, []);
    const closeDropdown = React.useCallback(() => {
        onCloseSubscribers.current.forEach((x) => x());
        setIsOpen(false);
    }, [setIsOpen]);
    const toggleDropdown = React.useCallback(() => {
        setIsOpen((prev) => {
            const next = !prev;
            next ? onOpenSubscribers.current.forEach((x) => x()) : onCloseSubscribers.current.forEach((x) => x());
            return next;
        });
    }, [setIsOpen]);
    React.useEffect(() => {
        if (!isOpen || buttonElement === null || dropdownElement === null)
            return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeDropdown();
                buttonElement?.focus();
                event.preventDefault();
            }
        };
        const onFocusInOrPointerDown = (event: FocusEvent | PointerEvent) => {
            if (isHTMLElement(event.target) && !buttonElement.contains(event.target) && !dropdownElement.contains(event.target)) {
                closeDropdown();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("focusin", onFocusInOrPointerDown);
        window.addEventListener("pointerdown", onFocusInOrPointerDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("focusin", onFocusInOrPointerDown);
            window.removeEventListener("pointerdown", onFocusInOrPointerDown);
        };
    }, [isOpen, buttonElement, dropdownElement, closeDropdown]);
    const buttonAriaAttributes: ButtonAriaAttributes = {
        "aria-expanded": isOpen,
        "aria-haspopup": dropdownAriaRole,
        "aria-controls": isOpen ? id.current : undefined,
    };
    const dropdownAttributes: DropdownAttributes = {
        dropdownId: id.current,
        dropdownAriaAttributes: {
            role: dropdownAriaRole,
        },
    };
    return {
        isOpen,
        closeDropdown,
        toggleDropdown,
        buttonElement,
        setButtonRef: setButtonElement,
        buttonAriaAttributes,
        setDropdownRef: (dropdown: HTMLDivElement) => {
            setDropdownElement(dropdown);
            if (trapFocus) {
                setFocusTrapElement(dropdown);
            }
        },
        dropdownAttributes,
        subscribeOpen,
        subscribeClose,
    };
}
export interface DropdownButtonState {
    isOpen: boolean;
    toggleDropdown: () => void;
    closeDropdown: () => void;
    buttonElement: HTMLButtonElement | null;
    setButtonRef: (button: HTMLButtonElement) => void;
    buttonAriaAttributes: ButtonAriaAttributes;
    setDropdownRef: (dropdown: HTMLDivElement) => void;
    dropdownAttributes: DropdownAttributes;
    subscribeOpen: (subscriber: OpenDropdownCallback) => CleanupSubscription;
    subscribeClose: (subscriber: CloseDropdownCallback) => CleanupSubscription;
}
interface ButtonAriaAttributes {
    "aria-expanded": boolean;
    "aria-haspopup": PopoverAriaRole;
    "aria-controls": string | undefined;
}
interface DropdownAttributes {
    dropdownId: string;
    dropdownAriaAttributes: {
        role: PopoverAriaRole;
    };
}
type CloseDropdownCallback = () => void;
type OpenDropdownCallback = () => void;
type CleanupSubscription = () => void;
